import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  CSSProperties,
  ChangeEvent,
  FC,
  useEffect,
  useRef,
  useState,
} from "react";
import { fetchUser } from "../../../redux/userSlice";
import Layout from "../Layout";
import {
  availableDatasets,
  clearCache,
  currentDatasets,
  refreshDataset,
  reloadSchema,
  setConfirmPopUp,
  hideConfirmPopup,
  refreshDatasetLogs,
  dataloadLogs,
  dataloadProcessLogs,
  triggerLoad,
  apiVersion,
  uploadTrainingVideo,
  updateTrainingVideo,
  fetchTrainingVideos,
  deleteTrainingVideo,
} from "../../../redux/adminSlice";
import type { TrainingVideoResponse } from "../../../redux/adminSlice";
import { AgGridReact } from "ag-grid-react";
import { Button } from "../../../components/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faBroom, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { showNotification } from "../../../redux/notificationSlice";
import ConfirmPopup from "../../../components/ConfirmPopup";
import { useModal } from "../../../components/Modal";
import { ITooltipParams } from "ag-grid-community";
import axiosInstance from "../../../api";
import TrainingVideoModal, {
  TrainingVideoDraft,
} from "./TrainingVideoModal";

type HelpDocType = "glossary" | "faq" | "productGuide";

type HelpDocConfig = {
  key: HelpDocType;
  title: string;
  description: string;
  uploadPath: string;
};

const trainingCategoryOptions = [
  { value: "GETTING_STARTED", label: "Getting Started" },
  { value: "PRODUCTS", label: "Products" },
  { value: "REPORTS", label: "Reports" },
  { value: "ADVANCED", label: "Advanced" },
] as const;

type TrainingVideo = {
  id: number;
  fileName: string;
  title: string;
  category: string;
  description: string;
};

const helpDocumentConfigs: HelpDocConfig[] = [
  {
    key: "glossary",
    title: "Glossary Document",
    description: "Upload definitions and terminology in PDF format.",
    uploadPath: "glossary",
  },
  {
    key: "faq",
    title: "FAQ Document",
    description: "Upload frequently asked questions in PDF format.",
    uploadPath: "faq",
  },
  {
    key: "productGuide",
    title: "Product Guide Document",
    description: "Upload product walkthroughs and usage guides in PDF format.",
    uploadPath: "productGuide",
  },
];

const mapTrainingVideo = (video: TrainingVideoResponse): TrainingVideo => ({
  id: video.trainingVideoId,
  fileName: video.fileKey.split(/[\\/]/).pop() || video.fileKey,
  title: video.title,
  category: video.category,
  description: video.description,
});

type Dataset = {
  dataType: string;
  reportDate: string;
  refresh: boolean;
  // Add any other properties that 'avail' might have if needed
};

export const CustomButton: FC<{ data: Dataset }> = ({ data }) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleButtonClick = (action: string) => {
    if (action === "refresh") {
      // Dispatch the refreshDataset action
      //setShowConfirmPopup(true);
      dispatch(
        setConfirmPopUp({
          title: "Refresh Dataset",
          message: `Are you sure you want to refresh ${data.dataType}?`,
          confirmAction: (title: string, id: number) => {
            dispatch(refreshDataset(data.dataType)).then(() => {
              dispatch(refreshDatasetLogs());
            });
          },
          closeAction: () => {
            dispatch(hideConfirmPopup({}));
          },
          id: 0,
        }),
      );
    } else if (action === "clearCache") {
      dispatch(
        setConfirmPopUp({
          title: "Clear Cache",
          message: `Are you sure you want to clear cache for ${data.dataType}?`,
          confirmAction: (title: string, id: number) => {
            dispatch(clearCache(data.dataType))
              .then(() => {
                dispatch(refreshDatasetLogs());
              })
              .catch((error: any) => {
                if (error?.status === 504 || error?.response?.status === 504) {
                  dispatch(
                    showNotification({
                      message:
                        "Request sent to clear cache, please check back after sometime.",
                      duration: 3000,
                      isError: false,
                    }),
                  );
                }
              });
          },
          closeAction: () => {
            dispatch(hideConfirmPopup({}));
          },
          id: 0,
        }),
      );
    } else if (action === "reloadSchema") {
      dispatch(
        setConfirmPopUp({
          title: "Reload Schema",
          message: `Are you sure you want to reload all schema for ${data.dataType}?`,
          confirmAction: (title: string, id: number) => {
            dispatch(reloadSchema(data.dataType)).then(() => {
              dispatch(refreshDatasetLogs());
            });
          },
          closeAction: () => {
            dispatch(hideConfirmPopup({}));
          },
          id: 0,
        }),
      );
    } else if (action === "triggerLoad") {
      dispatch(
        setConfirmPopUp({
          title: "Trigger Load",
          message: `Are you sure you want to trigger load for ${data.dataType}?`,
          confirmAction: (title: string, id: number) => {
            dispatch(triggerLoad(data.dataType)).then(() => {
              dispatch(dataloadLogs());
            });
          },
          closeAction: () => {
            dispatch(hideConfirmPopup({}));
          },
          id: 0,
        }),
      );
    }
  };

  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <Button
        onClick={() => handleButtonClick("refresh")}
        color="secondary"
        className="btn md icon-primary-text"
        /*className={data.refresh ? "btn md" : "btn md disabled-icon"}*/ variant="plain"
        title="Refresh Dataset"
      >
        <FontAwesomeIcon
          icon={faRefresh} /*className={data.refresh ? '' : 'disabled-icon'}*/
        />
      </Button>
      <Button
        onClick={() => handleButtonClick("clearCache")}
        color="secondary"
        className="btn md icon-primary-text"
        variant="plain"
        title="Clear Cache"
      >
        <FontAwesomeIcon icon={faBroom} />
      </Button>
      <Button
        onClick={() => handleButtonClick("reloadSchema")}
        color="secondary"
        className="btn md icon-primary-text"
        variant="plain"
        title="Reload Schema"
      >
        <FontAwesomeIcon icon={faRotateRight} />
      </Button>
      {/* <Button
        onClick={() => handleButtonClick("triggerLoad")}
        color="secondary"
        className="btn md"
        variant="plain"
        title="Trigger Load"
      >
        <FontAwesomeIcon icon={faUpload} />
      </Button> */}
    </div>
  );
};

const Admin = (props: any) => {
  const currentUser = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch<AppDispatch>();
  const [isAdmin, setAdmin] = useState(false);
  const currentDataset = useSelector(
    (state: RootState) => state.admin.currentDatasets,
  );
  const availableDataset = useSelector(
    (state: RootState) => state.admin.availableDatasets,
  );
  const [style, setStyle] = useState<CSSProperties>({
    height: "220px",
    width: "910px",
  });
  const [refreshStyle, setRefreshStyle] = useState<CSSProperties>({
    height: "400px",
  });
  const loading = useSelector((state: RootState) => state.admin.loading);
  const status = useSelector((state: RootState) => state.admin.status);
  const [isLoading, setIsLoading] = useState(loading);

  const confirmPopUp = useSelector(
    (state: RootState) => state.admin.confirmPopUp,
  );
  const isConfirmPopupVisible = useSelector(
    (state: RootState) => state.admin.showConfirmPopup,
  );

  const refreshData = useSelector(
    (state: RootState) => state.admin.refreshDatasetLogs,
  );
  const [refreshDataset, setRefreshDataset] = useState<any[]>(refreshData);

  const dataloadData = useSelector(
    (state: RootState) => state.admin.dataloadLogs,
  );
  const [dataloadProcessData, setDataloadProcessData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact<any>>(null);

  const [selectedFiles, setSelectedFiles] = useState<Record<HelpDocType, File | null>>({
    glossary: null,
    faq: null,
    productGuide: null,
  });
  const [isUploadingDoc, setIsUploadingDoc] = useState<Record<HelpDocType, boolean>>({
    glossary: false,
    faq: false,
    productGuide: false,
  });
  const [selectedHelpDocType, setSelectedHelpDocType] = useState<HelpDocType>(
    "glossary",
  );
  const helpDocInputRef = useRef<HTMLInputElement>(null);
  const [trainingVideos, setTrainingVideos] = useState<TrainingVideo[]>([]);
  const [isLoadingTrainingVideos, setIsLoadingTrainingVideos] = useState(false);
  const [isDeletingTrainingVideo, setIsDeletingTrainingVideo] = useState(false);
  const [trainingVideosError, setTrainingVideosError] = useState(false);
  const videoDraftRef = useRef<TrainingVideoDraft>({
    editingVideoId: null,
    existingFileName: "",
    videoFile: null,
    title: "",
    category: trainingCategoryOptions[0].value,
    description: "",
  });

  const uiVersion = localStorage.getItem("uiVersion") || "";
  const [apiVer, setApiVersion] = useState("");

  const selectedHelpDocConfig =
    helpDocumentConfigs.find((docConfig) => docConfig.key === selectedHelpDocType) ||
    helpDocumentConfigs[0];

  const loadTrainingVideoList = async (showErrorNotification = true) => {
    setIsLoadingTrainingVideos(true);
    setTrainingVideosError(false);
    try {
      const videos = await dispatch(fetchTrainingVideos()).unwrap();
      setTrainingVideos(videos.map(mapTrainingVideo));
      return true;
    } catch (error) {
      setTrainingVideosError(true);
      if (showErrorNotification) {
        dispatch(
          showNotification({
            message: "Error loading training videos. Please contact admin.",
            duration: 3000,
            isError: true,
          }),
        );
      }
      return false;
    } finally {
      setIsLoadingTrainingVideos(false);
    }
  };

  const resetVideoForm = () => {
    videoDraftRef.current = {
      editingVideoId: null,
      existingFileName: "",
      videoFile: null,
      title: "",
      category: trainingCategoryOptions[0].value,
      description: "",
    };
  };

  const openAddVideoForm = () => {
    resetVideoForm();
    openTrainingVideoModal({ closeOnClickBackdrop: false });
  };

  const openEditVideoForm = (video: TrainingVideo) => {
    videoDraftRef.current = {
      editingVideoId: video.id,
      existingFileName: video.fileName,
      videoFile: null,
      title: video.title,
      category: video.category,
      description: video.description,
    };
    openTrainingVideoModal({ closeOnClickBackdrop: false });
  };

  const handleRemoveVideo = (video: TrainingVideo) => {
    dispatch(
      setConfirmPopUp({
        title: "Remove Training Video",
        message: `Are you sure you want to remove "${video.title}"?`,
        confirmAction: async () => {
          setIsDeletingTrainingVideo(true);
          try {
            await dispatch(deleteTrainingVideo(video.id)).unwrap();
            setTrainingVideos((prevVideos) =>
              prevVideos.filter((currentVideo) => currentVideo.id !== video.id),
            );
            dispatch(hideConfirmPopup({}));
            dispatch(
              showNotification({
                message: "Training video removed successfully.",
                duration: 2500,
              }),
            );
            await loadTrainingVideoList(false);
          } catch (error) {
            dispatch(
              showNotification({
                message: "Error removing training video. Please contact admin.",
                duration: 3000,
                isError: true,
              }),
            );
          } finally {
            setIsDeletingTrainingVideo(false);
          }
        },
        closeAction: () => {
          dispatch(hideConfirmPopup({}));
        },
        id: video.id,
      }),
    );
  };

  const cancelVideoFormState = () => {
    resetVideoForm();
  };

  const handleVideoSave = async (draft: TrainingVideoDraft) => {
    const isEditMode = draft.editingVideoId !== null;

    if (!draft.title.trim()) {
      dispatch(
        showNotification({
          message: "Please provide a training video title.",
          duration: 3000,
          isError: true,
        }),
      );
      return false;
    }

    if (!draft.description.trim()) {
      dispatch(
        showNotification({
          message: "Please provide a training video description.",
          duration: 3000,
          isError: true,
        }),
      );
      return false;
    }

    if (!isEditMode && !draft.videoFile) {
      dispatch(
        showNotification({
          message: "Please select a video file before saving.",
          duration: 3000,
          isError: true,
        }),
      );
      return false;
    }

    const formData = new FormData();
    if (draft.videoFile) {
      formData.append("file", draft.videoFile);
    }
    formData.append("title", draft.title.trim());
    formData.append("categoryType", draft.category);
    formData.append("description", draft.description.trim());
    if (isEditMode) {
      formData.append("replaceFile", String(Boolean(draft.videoFile)));
    }

    try {
      if (draft.editingVideoId !== null) {
        await dispatch(
          updateTrainingVideo({
            id: draft.editingVideoId,
            formData,
          }),
        ).unwrap();
      } else {
        const response = await dispatch(uploadTrainingVideo(formData)).unwrap();
        if (
          typeof response !== "string" ||
          response.trim().toLowerCase() !== "success"
        ) {
          dispatch(
            showNotification({
              message: "Training video could not be saved. Please try again.",
              duration: 3000,
              isError: true,
            }),
          );
          return false;
        }
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: isEditMode
            ? "Error updating training video. Please contact admin."
            : "Error uploading training video. Please contact admin.",
          duration: 3000,
          isError: true,
        }),
      );
      return false;
    }

    if (isEditMode) {
      setTrainingVideos((prevVideos) =>
        prevVideos.map((video) =>
          video.id === draft.editingVideoId
            ? {
                ...video,
                fileName: draft.videoFile
                  ? draft.videoFile.name
                  : draft.existingFileName,
                title: draft.title.trim(),
                category: draft.category,
                description: draft.description.trim(),
              }
            : video,
        ),
      );
      dispatch(
        showNotification({
          message: "Training video details updated.",
          duration: 2500,
        }),
      );
    } else {
      const newTrainingVideo: TrainingVideo = {
        id: Date.now(),
        fileName: draft.videoFile ? draft.videoFile.name : "",
        title: draft.title.trim(),
        category: draft.category,
        description: draft.description.trim(),
      };

      setTrainingVideos((prevVideos) => [newTrainingVideo, ...prevVideos]);
      dispatch(
        showNotification({
          message: "Training video added.",
          duration: 2500,
        }),
      );
    }

    await loadTrainingVideoList(false);
    cancelVideoFormState();
    return true;
  };

  const openTrainingVideoModal = useModal(
    <TrainingVideoModal
      getInitialDraft={() => videoDraftRef.current}
      trainingCategoryOptions={trainingCategoryOptions}
      onCancel={cancelVideoFormState}
      onSave={handleVideoSave}
    />,
    "trainingVideoModal",
  );

  const isPdfFile = (selectedFile: File) => {
    return (
      selectedFile.type === "application/pdf" ||
      selectedFile.name.toLowerCase().endsWith(".pdf")
    );
  };

  const getHelpDocTitle = (docType: HelpDocType) => {
    return (
      helpDocumentConfigs.find((docConfig) => docConfig.key === docType)?.title ||
      "Document"
    );
  };

  const handleFileChange = (
    docType: HelpDocType,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (!isPdfFile(selectedFile)) {
      setSelectedFiles((prevFiles) => ({ ...prevFiles, [docType]: null }));
      dispatch(
        showNotification({
          message: `Only PDF files are allowed for ${getHelpDocTitle(docType)}.`,
          duration: 3000,
          isError: true,
        }),
      );
      event.target.value = "";
      return;
    }

    setSelectedFiles((prevFiles) => ({ ...prevFiles, [docType]: selectedFile }));
  };

  const handleUpload = async (docType: HelpDocType) => {
    const fileToUpload = selectedFiles[docType];
    if (!fileToUpload) {
      dispatch(
        showNotification({
          message: `Please select a PDF file for ${getHelpDocTitle(docType)} first.`,
          duration: 3000,
          isError: true,
        }),
      );
      return;
    }

    if (!isPdfFile(fileToUpload)) {
      dispatch(
        showNotification({
          message: "Only PDF files are allowed.",
          duration: 3000,
          isError: true,
        }),
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);
    const uploadPath =
      helpDocumentConfigs.find((docConfig) => docConfig.key === docType)?.uploadPath ||
      docType;

    try {
      setIsUploadingDoc((prevState) => ({ ...prevState, [docType]: true }));
      await axiosInstance.post(
        `/rest/admin/uploadHelpDocument/${uploadPath}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setSelectedFiles((prevFiles) => ({ ...prevFiles, [docType]: null }));
      if (helpDocInputRef.current) {
        helpDocInputRef.current.value = "";
      }
      dispatch(
        showNotification({
          message: `${getHelpDocTitle(docType)} uploaded successfully.`,
          duration: 3000,
        }),
      );
    } catch (error) {
      dispatch(
        showNotification({
          message: `Error uploading ${getHelpDocTitle(docType)}. Please contact admin.`,
          duration: 3000,
          isError: true,
        }),
      );
    } finally {
      setIsUploadingDoc((prevState) => ({ ...prevState, [docType]: false }));
    }
  };

  const refreshDatasetColumns = [
    { headerName: "Id", field: "id", cellClass: "text-center", width: 50 },
    {
      headerName: "Started By",
      field: "userName",
      cellClass: "text-center",
      width: 250,
    },
    {
      headerName: "Action",
      field: "action",
      cellClass: "text-center",
      width: 120,
    },
    {
      headerName: "Data Type",
      field: "dataType",
      cellClass: "text-center",
      width: 100,
    },
    {
      headerName: "Status",
      field: "status",
      cellClass: "text-center",
      width: 100,
    },
    {
      headerName: "Start Time",
      field: "startTime",
      cellClass: "text-center",
      valueGetter: (params: any) => {
        return params.data?.startTime
          ? formatDateTime(new Date(params.data.startTime))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDateTime(new Date(params.value)) : "";
      },
    },
    {
      headerName: "End Time",
      field: "endTime",
      cellClass: "text-center",
      valueGetter: (params: any) => {
        return params.data?.endTime
          ? formatDateTime(new Date(params.data.endTime))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDateTime(new Date(params.value)) : "";
      },
    },
    {
      headerName: "Message",
      headerStyle: "text-left",
      field: "comments",
      width: 700,
      cellClass: "text-left",
      tooltipValueGetter: (p: ITooltipParams) => p.value,
    },
  ];

  const dataloadLogColumns = [
    { headerName: "Id", field: "executionId", cellClass: "text-center" },
    { headerName: "Status", field: "status", cellClass: "text-center" },
    {
      headerName: "Start Time",
      field: "startTime",
      cellClass: "text-center",
      valueGetter: (params: any) => {
        return params.data?.startTime
          ? formatDateTime(new Date(params.data.startTime))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDateTime(new Date(params.value)) : "";
      },
    },
    {
      headerName: "End Time",
      field: "endTime",
      cellClass: "text-center",
      valueGetter: (params: any) => {
        return params.data?.endTime
          ? formatDateTime(new Date(params.data.endTime))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDateTime(new Date(params.value)) : "";
      },
    },
  ];

  const dataloadProcessLogColumns = [
    {
      headerName: "Process Name",
      field: "processName",
      cellClass: "text-center",
    },
    { headerName: "Status", field: "status", cellClass: "text-center" },

    {
      headerName: "Start Time",
      field: "startTime",
      cellClass: "text-center",
      valueGetter: (params: any) => {
        return params.data?.startTime
          ? formatDateTime(new Date(params.data.startTime))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDateTime(new Date(params.value)) : "";
      },
    },
    {
      headerName: "End Time",
      field: "endTime",
      cellClass: "text-center",
      valueGetter: (params: any) => {
        return params.data?.endTime
          ? formatDateTime(new Date(params.data.endTime))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDateTime(new Date(params.value)) : "";
      },
    },
    {
      headerName: "Error Message",
      field: "errorMessage",
      tooltipValueGetter: (p: ITooltipParams) => p.value,
    },
    { headerName: "Table Name", field: "tableName", cellClass: "text-center" },
  ];

  const columns = [
    { headerName: "Dataset", field: "dataType", cellClass: "text-center" },
    {
      headerName: "Current Month",
      cellClass: "text-center",
      field: "createdDate",
      valueGetter: (params: any) => {
        return params.data?.currentMonth
          ? formatDate(new Date(params.data.currentMonth))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDate(new Date(params.value)) : "";
      },
    },
    {
      headerName: "Available Month",
      cellClass: "text-center",
      field: "createdDate",
      valueGetter: (params: any) => {
        return params.data?.reportDate
          ? formatDate(new Date(params.data.reportDate))
          : null;
      },
      valueFormatter: (params: any) => {
        return params.value ? formatDate(new Date(params.value)) : "";
      },
    },
    {
      field: "action",
      headerName: "",
      cellRenderer: CustomButton,
      width: 240,
    },
  ];

  const [data, setData] = useState<Dataset[]>([]);
  function formatDate(date: any) {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  function formatDateTime(date: any) {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  useEffect(() => {
    if (currentUser.firstName === "") {
      dispatch(fetchUser()).then((response) => {
        if (response.payload.features.indexOf("Admin") !== -1) {
          setAdmin(true);
          dispatch(currentDatasets());
          dispatch(availableDatasets());
          dispatch(refreshDatasetLogs());
          dispatch(dataloadLogs());
          dispatch(apiVersion()).then((response) => {
            setApiVersion(response.payload);
          });
        }
      });
    } else {
      if (currentUser.features.indexOf("Admin") !== -1) {
        setAdmin(true);
        dispatch(currentDatasets());
        dispatch(availableDatasets());
        dispatch(refreshDatasetLogs());
        dispatch(dataloadLogs());
        dispatch(apiVersion()).then((response) => {
          setApiVersion(response.payload);
        });
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAdmin) {
      loadTrainingVideoList();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      if (currentDataset && availableDataset && availableDataset.length > 0) {
        const combinedData = availableDataset.map((avail) => {
          const current = currentDataset.find(
            (current) => avail.dataType === current.dataType,
          );
          return {
            ...avail,
            ...(current && avail.reportDate !== current.reportDate
              ? { refresh: true, currentMonth: current.reportDate }
              : {
                  refresh: false,
                  currentMonth: current ? current.reportDate : "",
                }),
          };
        });
        setData(combinedData);
      }
    }
  }, [currentDataset, availableDataset, dispatch]);

  useEffect(() => {
    if (loading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    if (status === "success") {
      dispatch(
        showNotification({
          message: "Operation executed successfully on all nodes",
          duration: 3000,
        }),
      );
    } else if (status === "error") {
      dispatch(
        showNotification({
          message: "Operation failed",
          duration: 3000,
          isError: true,
        }),
      );
    }
  }, [status]);

  useEffect(() => {
    if (refreshData && refreshData.length > 0) {
      setRefreshDataset(refreshData);
    }
  }, [refreshData]);

  const onSelectionChanged = () => {
    if (
      gridRef &&
      gridRef.current &&
      gridRef.current.api &&
      gridRef.current.api.getSelectedRows().length > 0
    ) {
      const executionId = gridRef.current.api.getSelectedRows()[0].executionId;
      dispatch(dataloadProcessLogs(executionId)).then((result: any) => {
        setDataloadProcessData(result.payload);
      });
    }
  };

  const getRowClass = (params: any) => {
    if (params.data.status === "FAILED") {
      return "row-danger";
    } else if (
      params.data.status === "SUCCESS" ||
      params.data.status === "COMPLETED"
    ) {
      return "row-green";
    }
    return "";
  };

  const isBusy =
    isLoading ||
    isDeletingTrainingVideo ||
    Object.values(isUploadingDoc).some((isUploading) => isUploading);

  return (
    <Layout>
      <div className="admin-container">
        <div className="admin-overview-card">
          <div className="admin-overview-headline">
            <h2>Admin Dashboard</h2>
              <span className="admin-version-chip">
            UI Version: {uiVersion} | API Version: {apiVer}
          </span>
          </div>
        
          {isAdmin && (
            <p className="admin-overview-message">
              Welcome to the admin dashboard. Here you can load data sets, see
              logs of dataload, and manage help documents.
            </p>
          )}
          {!isAdmin && (
            <p className="admin-overview-message admin-overview-warning">
              You do not have admin privileges to access this dashboard.
            </p>
          )}
        </div>
        <div className="help-docs-upload-section">
          <div className="help-docs-upload-heading">
            <h3>Upload Help Documents</h3>
          </div>
          <div className="help-docs-upload-grid">
            <div className="help-doc-upload-card help-doc-upload-card-single">
              {/* <h4>Help Document</h4> */}
              <label className="help-doc-type-label" htmlFor="help-doc-type-select">
                Document Type
              </label>
              <select
                id="help-doc-type-select"
                className="help-doc-type-select"
                value={selectedHelpDocType}
                onChange={(event) =>
                  setSelectedHelpDocType(event.target.value as HelpDocType)
                }
              >
                {helpDocumentConfigs.map((docConfig) => (
                  <option key={docConfig.key} value={docConfig.key}>
                    {docConfig.title}
                  </option>
                ))}
              </select>
            

              <input
                key={selectedHelpDocType}
                ref={helpDocInputRef}
                type="file"
                style={{ marginTop: "10px" }}
                accept="application/pdf,.pdf"
                onChange={(event) => handleFileChange(selectedHelpDocType, event)}
              />
              <div className="help-doc-upload-footer">
              
                <Button
                  onClick={() => handleUpload(selectedHelpDocType)}
                  className="btn md"
                  color="primary"
                  style={{ marginTop: "5px", minHeight: "30px" }}
                  variant="solid"
                  disabled={
                    !selectedFiles[selectedHelpDocType] ||
                    isUploadingDoc[selectedHelpDocType]
                  }
                >
                  {isUploadingDoc[selectedHelpDocType] ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>
          </div>
          <div className="training-video-section">
            <div className="training-video-header">
              <div>
                <h3>Training Videos</h3>
                <p>List of uploaded videos.</p>
              </div>
              <Button
                onClick={openAddVideoForm}
                className="btn md"
                color="primary"
                variant="solid"
              >
                Add Video
              </Button>
            </div>

            {isLoadingTrainingVideos ? (
              <div className="training-video-empty-state">
                <LoadingOverlay show height={100} />
              </div>
            ) : trainingVideosError ? (
              <div className="training-video-empty-state">
                <p>Training videos could not be loaded.</p>
                <Button
                  onClick={() => loadTrainingVideoList()}
                  className="btn md"
                  color="primary"
                  variant="outline"
                >
                  Retry
                </Button>
              </div>
            ) : trainingVideos.length === 0 ? (
              <div className="training-video-empty-state">
                No training videos added yet.
              </div>
            ) : (
              <div className="training-video-list">
                {trainingVideos.map((video) => (
                  <div key={video.id} className="training-video-item">
                    <div className="training-video-info compact">
                      
                      <h4 title={video.title}>{video.title}</h4>
                    
                      
                      <p className="training-video-description" title={video.description}>
                        {video.description}
                      </p>
                      <p className="training-video-category">
                        {trainingCategoryOptions.find(
                          (category) => category.value === video.category,
                        )?.label ?? video.category}
                      </p>
                    </div>
                    <div className="training-video-actions">
                      <Button
                        onClick={() => openEditVideoForm(video)}
                        className="btn md training-video-btn training-video-btn-edit"
                        color="primary"
                        variant="outline"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleRemoveVideo(video)}
                        className="btn md training-video-btn training-video-btn-remove"
                        color="primary"
                        variant="outline"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
        {isAdmin && data.length > 0 && (
          <div className="dataset-list admin-data-sections">
            <div className="admin-data-card">
              <div className="admin-data-header">
                <h3>Datasets</h3>
            
              </div>
              <div className="ag-theme-alpine grid-wrapper saved-queries admin-grid-shell">
                <div style={style}>
                  <AgGridReact
                    rowData={data}
                    columnDefs={columns as any}
                    suppressContextMenu={true}
                  />
                </div>
              </div>
            </div>
            {refreshDataset && refreshDataset.length > 0 && (
              <div className="admin-data-card">
                <div className="admin-data-header">
                  <h3>Data Refresh Logs</h3>
            
                </div>
                <div className="ag-theme-alpine grid-wrapper saved-queries admin-grid-shell">
                  <div style={refreshStyle}>
                    <AgGridReact
                      rowData={refreshDataset}
                      columnDefs={refreshDatasetColumns as any}
                      suppressContextMenu={true}
                      suppressAutoSize={false}
                      getRowClass={getRowClass}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="admin-data-card">
              <div className="admin-data-header">
                <h3>Data Load Logs</h3>
            
              </div>
              <div className="ag-theme-alpine grid-wrapper saved-queries admin-grid-shell">
                <div style={style}>
                  <AgGridReact
                    rowData={dataloadData}
                    columnDefs={dataloadLogColumns as any}
                    suppressContextMenu={true}
                    rowSelection={"single"}
                    onSelectionChanged={onSelectionChanged}
                    ref={gridRef}
                    getRowClass={getRowClass}
                  />
                </div>
              </div>
            </div>
            {dataloadProcessData && dataloadProcessData.length > 0 && (
              <div className="admin-data-card">
                <div className="admin-data-header">
                  <h3>
                    Data Load Process Logs for Execution Id{" "}
                    {dataloadProcessData[0].executionId}
                  </h3>
                  <p className="admin-data-subtitle">
                    Detailed process steps, timing, and errors for the selected load.
                  </p>
                </div>
                <div className="ag-theme-alpine grid-wrapper saved-queries admin-grid-shell">
                  <div style={refreshStyle}>
                    <AgGridReact
                      rowData={dataloadProcessData}
                      columnDefs={dataloadProcessLogColumns as any}
                      suppressContextMenu={true}
                      getRowClass={getRowClass}
                    />
                  </div>
                </div>
              </div>
            )}
            <div style={{ height: 30 }}></div>
          </div>
        )}
        <div className="admin-global-loader">
          <LoadingOverlay show={isBusy} />
        </div>
      </div>
      {isConfirmPopupVisible && (
        <ConfirmPopup
          title={confirmPopUp.title}
          id={confirmPopUp.id}
          message={confirmPopUp.message}
          confirmAction={confirmPopUp.confirmAction}
          closeAction={confirmPopUp.closeAction}
        ></ConfirmPopup>
      )}
    </Layout>
  );
};

export default Admin;
